import { createApp } from "vue";
import PipPlayer from "@/components/Streaming/PipPlayer.vue";
import store from "@/store";

class PipService {
    constructor() {
        this.pipWindow = null;

        this.playerEl = null;
        this.playerParent = null;

        this.previewCanvas = null;
        this.previewCanvasParent = null;

        this.root = null;
        this.app = null;

        this.onPipClose = null;

        this.resizeHandler = null;
        this.mouseEnterHandler = null;
        this.mouseLeaveHandler = null;
        this.pageHideHandler = null;

        this.hideTimer = null;

        this.progressAnimationFrame = null;
        this.progressAnimating = false;
    }
    async open(
        videoPlayer,
        previewCanvas,
        width = 800,
        height = 600,
        feedback,
        onPipOpen,
        onPipClose,
    ) {
        if (this.pipWindow) {
            return;
        }

        const playerEl = videoPlayer.el();
        if (!playerEl) {
            console.error("PiP player element 없음");
            return;
        }
        const pipWindow = await window.documentPictureInPicture.requestWindow({
            width: Math.round(width),
            height: Math.round(height)
        });

        this.pipWindow = pipWindow;
        this.onPipClose = onPipClose;

        this.playerEl = playerEl;
        this.playerParent = playerEl.parentNode;

        this.previewCanvas = previewCanvas;
        this.previewCanvasParent = previewCanvas.parentNode;

        await this.copyStyles();
        const pipDoc = this.pipWindow.document;

        /*
            root
        */
        const root = pipDoc.createElement("div");
        root.className = "pip-root";
        pipDoc.body.appendChild(root);

        this.root = root;

        /*
            video
        */
        const videoContainer = pipDoc.createElement("div");
        videoContainer.className = "pip-video";
        root.appendChild(videoContainer);

        playerEl.style.width = "100%";
        playerEl.style.height = "100%";

        videoContainer.appendChild(playerEl);
        videoContainer.appendChild(previewCanvas);

        requestAnimationFrame(() => {
            playerEl.style.width = "";
            playerEl.style.height = "";
        });

        /*
            Vue controls
        */
        const mountEl = pipDoc.createElement("div");
        mountEl.className ="pip-player";
        root.appendChild(mountEl);

        this.app = createApp(PipPlayer);
        this.app.provide("pipVideoPlayer", videoPlayer);
        this.app.provide("pipWindow", this.pipWindow);
        this.app.provide("pipFeedback", feedback);

        this.app.use(store);
        this.app.mount(mountEl);

        const playerWrapper = videoContainer;

        /*
            CSS
        */

        const style = pipDoc.createElement("style");

        style.textContent = `
        html,
        body {
            margin:0;
            padding:0;
            width:100%;
            height:100%;
            overflow:hidden;
            background:black;
        }
        .pip-root {
            position:fixed;
            inset:0;
            width:100vw;
            height:100vh;
            overflow:hidden;
            background:black;
        }
        .pip-video {
            position:absolute;
            inset:0;
            width:100%;
            height:100%;
            display:flex;
            align-items:center;
            justify-content:center;
            background:black;
        }
        .pip-video .video-js {
            width:100% !important;
            height:100% !important;
            background:black !important;
        }
        .pip-video .vjs-tech {
            width:100% !important;
            height:100% !important;
            object-fit:contain !important;
        }
        .pip-player {
            opacity:0;
            transition:opacity .2s;
            pointer-events:none;
        }
        .pip-player.show {
            opacity:1;
        }
        .pip-player > * {
            pointer-events:auto;
        }
        .pip-video .vjs-progress-control {
            flex: 1 1 auto !important;
            width: auto !important;
            min-width: 0 !important;
        }
        .pip-video .vjs-progress-holder {
            width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
        }
        .pip-preview-canvas {
            position: absolute;
            left: 0;
            bottom: 40px;

            width: 160px;
            height: 90px;

            z-index: 10000;

            pointer-events: none;

            background: #000;

            display: none;
        }
        `;

        pipDoc.head.appendChild(style);

        /*
            Video.js control 초기 숨김
        */

        setTimeout(() => {
            try {
                videoPlayer.userActive(false);
            } catch (e) { }
        }, 500);

        /*
            resize
        */

        this.resizeHandler = () => {
            requestAnimationFrame(() => {
                const controlBar = playerEl.querySelector(".vjs-control-bar");
                const progressControl = playerEl.querySelector(".vjs-progress-control");
                const progressHolder = playerEl.querySelector(".vjs-progress-holder");

                if (!controlBar || !progressControl || !progressHolder) {
                    return;
                }

                // control bar 실제 폭
                const controlBarWidth = controlBar.getBoundingClientRect().width;

                if (controlBarWidth <= 0) {
                    return;
                }

                // progress control이 남은 공간을 정확히 사용하도록
                progressControl.style.flex = "1 1 auto";
                progressControl.style.width = "auto";
                progressControl.style.minWidth = "0";

                // progress holder는 progress control 전체 사용
                progressHolder.style.width = "100%";
                progressHolder.style.marginLeft = "0";
                progressHolder.style.marginRight = "0";
            });
        };

        this.pipWindow.addEventListener("resize", this.resizeHandler);

        const getProgressElements = () => {
            const video = playerEl.querySelector("video");
            const progress = playerEl.querySelector(".vjs-play-progress");
            const controlBar = playerEl.querySelector(".vjs-control-bar");
            return {
                video,
                progress,
                controlBar,
            };
        };

        const isControlBarVisible = () => {
            const { controlBar } =
                getProgressElements();

            if (!controlBar) {
                return false;
            }

            const rect = controlBar.getBoundingClientRect();

            const style = pipDoc.defaultView.getComputedStyle(
                    controlBar
                );

            return (
                rect.width > 0 &&
                rect.height > 0 &&
                style.display !== "none" &&
                style.visibility !== "hidden" &&
                style.opacity !== "0"
            );
        };

        const updateProgress = () => {
            const {
                video,
                progress,
            } = getProgressElements();

            if (!video || !progress) {
                return;
            }

            const duration = video.duration;

            if (!Number.isFinite(duration) || duration <= 0) {
                return;
            }

            const percent = Math.max(
                0,
                Math.min(
                    100,
                    (video.currentTime / duration) * 100
                )
            );

            progress.style.width = `${percent}%`;
        };

        const stopProgressAnimation = () => {
            this.progressAnimating = false;
            if (this.progressAnimationFrame !== null) {
                try {
                    this.pipWindow.cancelAnimationFrame(
                        this.progressAnimationFrame
                    );
                } catch (e) {
                    // ignore
                }

                this.progressAnimationFrame = null;
            }
        };

        const animateProgress = () => {
            if (!this.progressAnimating) {
                return;
            }

            if (!isControlBarVisible()) {
                this.progressAnimationFrame =
                    this.pipWindow.requestAnimationFrame(
                        animateProgress
                    );

                return;
            }

            updateProgress();

            this.progressAnimationFrame =
                this.pipWindow.requestAnimationFrame(
                    animateProgress
                );
        };

        const startProgressAnimation = () => {
            if (!document.hidden) {
                return;
            }

            if (this.progressAnimating) {
                return;
            }

            if (videoPlayer.paused()) {
                return;
            }

            this.progressAnimating = true;

            updateProgress();

            this.progressAnimationFrame =
                this.pipWindow.requestAnimationFrame(
                    animateProgress
                );
        };

        document.addEventListener('visibilitychange', () => {
            if (!videoPlayer) return;

            if (document.visibilityState === 'hidden') {
                startProgressAnimation();
                this.mouseLeaveHandler();
            }
        });

        /*
            mouse control
        */
        this.mouseEnterHandler = () => {
            clearTimeout(this.hideTimer);
            mountEl.classList.add("show");
            try {
                videoPlayer.userActive(true);
            } catch (e) { }

            startProgressAnimation();
        };

        this.mouseLeaveHandler = () => {
            clearTimeout(this.hideTimer);
            this.hideTimer = setTimeout(() => {
                mountEl.classList.remove("show");
                try {
                    videoPlayer.userActive(false);
                } catch (e) { }

                stopProgressAnimation();
            }, 2000);
        };

        root.addEventListener("mouseenter", this.mouseEnterHandler);
        root.addEventListener("mouseleave", this.mouseLeaveHandler);

        /*
            close
        */
        this.pageHideHandler = async () => {
            await this.close();
            this.onClose?.();
        };

        this.pipWindow.addEventListener("pagehide", this.pageHideHandler);

        onPipOpen?.({
            pipWindow: this.pipWindow,
            previewCanvas: this.previewCanvas,
            playerWrapper,
        });
    }
    restoreVideo() {
        if (this.playerEl && this.playerParent) {
            this.playerParent.appendChild(this.playerEl);
        }
        if (this.previewCanvas && this.previewCanvasParent) {
            this.previewCanvasParent.appendChild(this.previewCanvas);
        }
    }
    async close() {
        clearTimeout(this.hideTimer);
        /*
            event 제거
        */
        if (this.pipWindow) {
            if (this.resizeHandler) {
                this.pipWindow.removeEventListener("resize", this.resizeHandler);
            }
            if (this.pageHideHandler) {
                this.pipWindow.removeEventListener("pagehide", this.pageHideHandler);
            }
            if (this.mouseEnterHandler) {
                this.root?.removeEventListener("mouseenter", this.mouseEnterHandler);
            }
            if (this.mouseLeaveHandler) {
                this.root?.removeEventListener("mouseleave", this.mouseLeaveHandler);
            }
        }

        this.progressAnimating = false;

        if (
            this.progressAnimationFrame !== null &&
            this.pipWindow
        ) {
            try {
                this.pipWindow.cancelAnimationFrame(
                    this.progressAnimationFrame
                );
            } catch (e) {
                // ignore
            }
        }

        this.progressAnimationFrame = null;

        if (this.app) {
            this.app.unmount();
            this.app = null;
        }

        this.restoreVideo();

        if (this.pipWindow) {
            this.pipWindow.close?.();
        }

        this.onPipClose?.();

        this.pipWindow = null;

        this.playerEl = null;
        this.playerParent = null;

        this.previewCanvas = null;
        this.previewCanvasParent = null;

        this.root = null;

        this.resizeHandler = null;
        this.mouseEnterHandler = null;
        this.mouseLeaveHandler = null;
        this.pageHideHandler = null;

        this.hideTimer = null;
        this.onPipClose = null;
    }
    async copyStyles() {
        if (!this.pipWindow)
            return;

        const pipDoc = this.pipWindow.document;

        for (const sheet of document.styleSheets) {
            try {
                const css = [...sheet.cssRules].map(r => r.cssText).join("\n");
                const style = pipDoc.createElement("style");
                style.textContent = css;
                pipDoc.head.appendChild(style);
            } catch (e) {
                // cross origin skip
            }
        }
    }
}
export default new PipService();